import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

import { Text } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import {
  initialErrorState,
  initialPaymentState,
  testCardPaymentState,
} from 'features/payment/initial-state';
import { CardType, IPaymentState } from 'features/payment/types';
import {
  getCCFormErrors,
  mapDeliveryToCCFormAddress,
  mapPaymentMethodCardTypes,
  onCCFormChange,
} from 'features/payment/utils';

import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { SafetechEncryption } from 'components/payments/integrations/orbital/components/encryption';
import {
  IEncryptionError,
  IEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import { toaster } from 'components/toaster/toaster';
import { VisuallyHidden } from 'components/ucl';
import useEffectOnce from 'hooks/use-effect-once';
import { getStaticPathname } from 'navigation/location/location';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import { AddEditMethodProps } from '../add-reload-prepaid-method-modal/types';
import CreditCardFormInputs from '../credit-card-form-inputs';
import { getErrorMessage } from '../credit-card-form-inputs/utils';

const useSupportedCardTypes = () => {
  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;

  return rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];
};

export type PaymentMethodRef = {
  addNewCard: () => void;
};

type AddEditPaymentMethodProps = AddEditMethodProps & {
  isPrepaidReload: boolean;
  setCanSubmit: React.Dispatch<React.SetStateAction<boolean>>;
};

export const AddEditPaymentMethod = forwardRef<PaymentMethodRef, AddEditPaymentMethodProps>(
  ({ openErrorDialog, setIsLoading, isLoading, isPrepaidReload = false, setCanSubmit }, ref) => {
    const { user } = useAuthContext();
    const [errors, setErrors] = useState(initialErrorState);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { loading: isUserLoading } = useLoyaltyUserId();

    const {
      accountToDelete: accountToUpdate,
      isOrbital,
      isQuickPayMethodSelected,
      addPaymentMethod,
      transformPaymentValues,
      setIsQuickPayMethodSelected,
      setIsAddPaymentMethodSuccess,
      storePaymentDetails,
      setCheckoutPaymentMethodId,
      setAccountToDelete,
      setEncryptionResult,
      setIsAddCreditCardModalVisible,
      setIsAddUpdateCardForReloadModalVisible,
    } = usePaymentContext();

    const paymentFieldVariations =
      useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
      defaultPaymentFieldVariation;

    const supportedCardTypes = useSupportedCardTypes();
    const { deliveryAddress } = useOrderContext();
    const { isDelivery: isDeliveryServiceMode } = useServiceModeContext();
    const pathname = getStaticPathname();
    const isDelivery =
      pathname.startsWith(routes.cart) ||
      (pathname.startsWith(routes.cartCheckout) && isDeliveryServiceMode);

    const { feCountryCode: billingCountry } = useLocale();
    const userDetailsName = user?.details.name;
    const [paymentValues, setPaymentValues] = useState<IPaymentState>(
      initialPaymentState({
        isDelivery,
        deliveryAddress,
        billingCountry,
        userDetailsName,
      })
    );

    const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
    const [isEncryptionActive, setIsEncryptionActive] = useState(false);

    useEffectOnce(() => {
      if (autoFill) {
        setPaymentValues(testCardPaymentState({ isDelivery, paymentFieldVariations }));
      }
    });

    useEffect(() => {
      setIsLoading(isLoading || isUserLoading);
    }, [isLoading, isUserLoading, setIsLoading]);

    const handleChange = useCallback(
      (name: any, value: any) => {
        const { state, formErrors } = onCCFormChange(
          name,
          value,
          paymentValues,
          errors,
          supportedCardTypes
        );
        if (value !== 'false') {
          setErrors(prevState => ({ ...prevState, ...formErrors }));
        }
        setPaymentValues(prevState => {
          return state.billingAddressSameAsDelivery
            ? {
                ...prevState,
                ...state,
                ...mapDeliveryToCCFormAddress(deliveryAddress),
              }
            : { ...prevState, ...state };
        });
      },
      [paymentValues, errors, deliveryAddress, supportedCardTypes]
    );

    const addNewCard = useCallback(
      async (event: any, encryptionResult?: IEncryptionResult) => {
        if (event) {
          event.preventDefault();
        }

        const onAddCardSuccess = () => {
          // Showing notifications
          toaster.positive(
            accountToUpdate ? t('cardUpdatedNotification') : t('cardAddedNotification')
          );
          setPaymentValues(initialPaymentState({ isDelivery, deliveryAddress }));

          if (isQuickPayMethodSelected) {
            setIsAddPaymentMethodSuccess(true);
            setIsQuickPayMethodSelected(false);
          }

          setIsAddCreditCardModalVisible(false);
          setIsAddUpdateCardForReloadModalVisible(false);

          setIsLoading(false);
        };

        const onAddCardFailure = (paymentMethodError: Error, message: string) => {
          logger.error({ error: paymentMethodError, message });
          openErrorDialog({
            message: t('paymentAddingError'),
            error: paymentMethodError,
          });
          setIsLoading(false);
        };

        setIsLoading(true);
        const reshapedPaymentValues = transformPaymentValues({
          paymentValues,
          paymentFieldVariations,
        });
        reshapedPaymentValues.accountToDelete = accountToUpdate || '';

        if (!paymentValues.saveCard) {
          // The automatic selection of this method is performed in the initPaymentMethods
          // function within the usePayment hook.
          storePaymentDetails({ state: paymentValues });
          setCheckoutPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_CARD);
          onAddCardSuccess();
          setAccountToDelete('');
          return;
        }

        try {
          await addPaymentMethod(
            reshapedPaymentValues,
            {
              skipErrorDialogOnError: true,
              isReloadMethod: isPrepaidReload,
            },
            encryptionResult
          );

          onAddCardSuccess();
          setAccountToDelete('');
        } catch (error) {
          onAddCardFailure(error, 'Error adding payment method');
        }
      },
      [
        setIsLoading,
        transformPaymentValues,
        paymentValues,
        paymentFieldVariations,
        accountToUpdate,
        isDelivery,
        deliveryAddress,
        isQuickPayMethodSelected,
        setIsAddCreditCardModalVisible,
        setIsAddUpdateCardForReloadModalVisible,
        setIsAddPaymentMethodSuccess,
        setIsQuickPayMethodSelected,
        openErrorDialog,
        storePaymentDetails,
        setCheckoutPaymentMethodId,
        setAccountToDelete,
        addPaymentMethod,
        isPrepaidReload,
      ]
    );

    // this is used only if its Orbital
    const onEncryptionResult = (data: IEncryptionResult) => {
      setEncryptionResult(data);
      setIsEncryptionActive(false);
      addNewCard(null, data);
    };

    // Handle encryption error, this is used only if its Orbital
    const onEncryptionError = (error: IEncryptionError) => {
      setIsEncryptionActive(false);
      setEncryptionResult(undefined);
      setIsLoading(false);
      const errorMessage = error.error;
      logger.error({ error: new Error(errorMessage), message: errorMessage });
      openErrorDialog({
        message: t('paymentAddingError'),
        error: new Error(errorMessage),
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        addNewCard: async () => {
          const { hasErrors, formErrors } = getCCFormErrors(
            paymentValues,
            errors,
            paymentFieldVariations,
            billingCountry
          );

          if (hasErrors) {
            // TODO: RN - Focus on first field with errors
            const newErrorMessage = getErrorMessage({ errors });
            setErrorMessage(newErrorMessage);

            return setErrors(prevState => ({
              ...prevState,
              ...formErrors,
              didAttemptSubmit: true,
            }));
          }

          if (isOrbital) {
            setEncryptionResult(undefined);
            setIsLoading(true);
            return setIsEncryptionActive(true);
          }
          addNewCard(null);
        },
      }),
      [
        addNewCard,
        billingCountry,
        errors,
        isOrbital,
        paymentFieldVariations,
        paymentValues,
        setEncryptionResult,
        setIsLoading,
      ]
    );

    return (
      <>
        {!!errorMessage && (
          <VisuallyHidden role="alert" accessibilityLabel="">
            <Text>{errorMessage}</Text>
          </VisuallyHidden>
        )}
        <CreditCardFormInputs
          onChange={handleChange}
          paymentValues={paymentValues}
          errors={errors}
          isDelivery={isDelivery}
          isPrepaidReload={isPrepaidReload}
          isUpdate={!!accountToUpdate}
          isQuickPayMethodSelected={isQuickPayMethodSelected}
        />
        {isOrbital && isEncryptionActive ? (
          <SafetechEncryption
            cardNumber={paymentValues.cardNumber}
            cvv={paymentValues.cvv || ''}
            onResult={onEncryptionResult}
            onError={onEncryptionError}
          />
        ) : null}
      </>
    );
  }
);
